import React, { createRef, useState, useEffect, forwardRef, useMemo } from 'react'
import { css, cx } from '@linaria/core'
import { useContainerWidth } from '@fs/zion-ui'

const hiddenCss = css`
  visibility: hidden;
  height: 0;
  overflow: hidden;
  max-width: 460px;
`

const gridzCss = css`
  display: grid;
  grid-gap: 16px;
`

const cardColumnCss = css`
  & > * + * {
    margin-top: 16px;
  }
`

const columnConfig = {
  3: 'repeat(3, minmax(calc(96% / 3), 460px))',
  2: 'repeat(2, minmax(calc(98.5% / 2), 460px))',
  1: 'repeat(1, 100%, 460px))',
}

const CardContainer = forwardRef(({ children }, ref) => {
  return <div ref={ref}>{children}</div>
})

export default function PersonMasonryLayout({ children }) {
  const [columns, setColumns] = useState(null)
  const atContainerWidth = useContainerWidth()

  const columnCount = atContainerWidth({
    default: 1,
    md: 2,
    xl: 3,
  })

  const wrappedChildren = useMemo(
    () =>
      React.Children.map(children, (child) => {
        const ref = createRef()
        return child ? <CardContainer ref={ref}>{child}</CardContainer> : null
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want this hook to trigger upon page load and when the window is resized, fixes TW-6852
    [columnCount]
  )

  // Reset columns when the columnCount changes, so the useEffect below can get a newly generated "wrappedChildren" array
  useEffect(() => {
    if (columns && columns.length !== columnCount) {
      setColumns(null)
    }
  }, [columns, columnCount])

  // Calculate which cards should go in which column based on height
  useEffect(() => {
    // This setTimeout is here to push setting the columns to the next event cycle.
    // Some cards need an initial cycle to properly calculate their inner contents.
    // If we calculate the heights before those are finished, then the heights get thrown off.
    const id = setTimeout(() => {
      const newColumns = Array.from({ length: columnCount }, () => ({
        height: 0,
        childs: [],
      }))
      wrappedChildren.forEach((kid) => {
        const shortestIndex = newColumns.reduce((acc, column, index) => {
          return newColumns[acc].height <= column.height ? acc : index
        }, 0)
        newColumns[shortestIndex].childs.push(kid)
        newColumns[shortestIndex].height += kid?.ref?.current?.clientHeight || 0
      })
      const needToUpdate =
        newColumns.map((col, index) => col.height !== columns?.[index]?.height).filter(Boolean)?.length > 0
      needToUpdate && setColumns(newColumns)
    }, 0)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want this hook to trigger upon page load and when the window is resized, fixes TW-6852
  }, [columns, columnCount])

  // When building for SSR, we want to just show the children right away, rather than doing any calculations or hiding any initial renders.
  if (process.env.SSR) return wrappedChildren

  return (
    <div>
      {/* The page initially renders this list of children so it can then calculate which cards to put in which column, by height */}
      {!columns && <div className={hiddenCss}>{wrappedChildren}</div>}

      {/* After the column calculations are done, the cards are properly rendered here */}
      {columns && (
        <div className={cx(columnCount > 1 && gridzCss)} style={{ gridTemplateColumns: columnConfig[columnCount] }}>
          {columns.map(({ childs }, index) => {
            const key = `column_${index}`
            return (
              <div className={cardColumnCss} key={key}>
                {childs}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
